import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { UUID } from 'crypto';

const getBlockedTankForTank = (stowagePlan: StowagePlan, tank: Tank): BlockedTank | undefined =>
    tank ? stowagePlan?.blockedTanks?.find((t) => t.tank && t.tank.tankId === tank.tankId) : undefined;

/** A blocked tank is orphaned when any of its boundary calls no longer exists in the rotation (e.g. deleted in another plan). */
export const isBlockedTankOrphaned = (blockedTank: BlockedTank, rotationTree: RotationPortCall[]): boolean => {
    if (!blockedTank || !rotationTree) return false;

    const portCallGuids = new Set(rotationTree.map((pc) => pc.portCallGuid));
    const berthCallGuids = new Set(rotationTree.flatMap((pc) => pc.berthCalls ?? []).map((bc) => bc.berthCallGuid));

    const portCallBoundaries = [
        blockedTank.fromPortCallGuid ?? blockedTank.fromPortCall?.portCallGuid,
        blockedTank.toPortCallGuid ?? blockedTank.toPortCall?.portCallGuid,
    ];
    const berthCallBoundaries = [
        blockedTank.fromBerthCallGuid ?? blockedTank.fromBerthCall?.berthCallGuid,
        blockedTank.toBerthCallGuid ?? blockedTank.toBerthCall?.berthCallGuid,
    ];

    return (
        portCallBoundaries.some((guid) => guid && !portCallGuids.has(guid)) ||
        berthCallBoundaries.some((guid) => guid && !berthCallGuids.has(guid))
    );
};

export const getOrphanedBlockedTank = (stowagePlan: StowagePlan, tank: Tank, rotationTree: RotationPortCall[]): BlockedTank | null => {
    const blockedTank = getBlockedTankForTank(stowagePlan, tank);
    return blockedTank && isBlockedTankOrphaned(blockedTank, rotationTree) ? blockedTank : null;
};

export const hasTankOrphanedBlock = (stowagePlan: StowagePlan, tank: Tank, rotationTree: RotationPortCall[]): boolean =>
    getOrphanedBlockedTank(stowagePlan, tank, rotationTree) !== null;

/** Single berth call where an orphaned block is reported: first berth call at or after the current port call, else the last berth call. */
export const getOrphanedBlockedTankAnchorBerthCall = (
    rotationTree: RotationPortCall[],
    currentPortCall: RotationPortCall | null
): RotationBerthCall | null => {
    const berthCallsOf = (pc: RotationPortCall) => pc.berthCalls?.filter((bc) => bc.berthId !== -1) ?? [];
    const currentIndex = currentPortCall ? rotationTree.findIndex((pc) => pc.portCallGuid === currentPortCall.portCallGuid) : -1;

    const fromCurrent = rotationTree.slice(Math.max(currentIndex, 0)).find((pc) => berthCallsOf(pc).length > 0);
    if (fromCurrent) return berthCallsOf(fromCurrent)[0];

    const lastWithBerths = [...rotationTree].reverse().find((pc) => berthCallsOf(pc).length > 0);
    if (!lastWithBerths) return null;
    const lastBerthCalls = berthCallsOf(lastWithBerths);
    return lastBerthCalls[lastBerthCalls.length - 1];
};

export const isTankBlocked = (
    stowagePlan: StowagePlan,
    tank: Tank,
    currentPortCall: RotationPortCall,
    currentBerthCall: RotationBerthCall,
    rotationTree: RotationPortCall[]
): boolean => {
    const blockedTank = getBlockedTankForTank(stowagePlan, tank);
    if (!blockedTank || isBlockedTankOrphaned(blockedTank, rotationTree)) return false;
    return isNodeInRange(rotationTree, blockedTank, currentPortCall, currentBerthCall);
};

const isNodeInRange = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    return (
        isAfterUpperBoundary(rotationTree, blockedTank, currentPortCall, currentBerthCall) &&
        isBeforeLowerBoundary(rotationTree, blockedTank, currentPortCall, currentBerthCall)
    );
};

const getPortCallIndex = (
    rotationTree: RotationPortCall[],
    portCallGuid: UUID) => rotationTree.findIndex((portCall) => portCall.portCallGuid === portCallGuid);

const getBerthCallIndex = (
    rotationTree: RotationPortCall[],
    berthCallGuid: UUID) => rotationTree.flatMap((pc) => pc.berthCalls).findIndex((berthCall) => berthCall.berthCallGuid === berthCallGuid);

const isAfterUpperBoundary = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    if (!currentPortCall) return false;

    const blockedPortCallIndex = getPortCallIndex(rotationTree, blockedTank.fromPortCall?.portCallGuid);
    const currentPortCallIndex = getPortCallIndex(rotationTree, currentPortCall.portCallGuid);

    if (currentPortCallIndex < blockedPortCallIndex) return false;
    if (currentPortCallIndex > blockedPortCallIndex) return true;

    if (!currentBerthCall || !blockedTank.fromBerthCall) return true;

    const blockedBerthCallIndex = getBerthCallIndex(rotationTree, blockedTank.fromBerthCall.berthCallGuid);
    const currentBerthCallIndex = getBerthCallIndex(rotationTree, currentBerthCall.berthCallGuid);

    return currentBerthCallIndex >= blockedBerthCallIndex;
};

const isBeforeLowerBoundary = (
    rotationTree: RotationPortCall[],
    blockedTank: BlockedTank,
    currentPortCall: RotationPortCall | null,
    currentBerthCall: RotationBerthCall | null
): boolean => {
    if (!currentPortCall) return false;

    if (!blockedTank.toPortCall) {
        return true;
    }

    const blockedPortCallIndex = getPortCallIndex(rotationTree, blockedTank.toPortCall.portCallGuid);
    const currentPortCallIndex = getPortCallIndex(rotationTree, currentPortCall.portCallGuid);

    if (currentPortCallIndex < blockedPortCallIndex) return true;
    if (currentPortCallIndex > blockedPortCallIndex) return false;

    if (!currentBerthCall || !blockedTank.toBerthCall) return true;

    const blockedBerthCallIndex = getBerthCallIndex(rotationTree, blockedTank.toBerthCall.berthCallGuid);
    const currentBerthCallIndex = getBerthCallIndex(rotationTree, currentBerthCall.berthCallGuid);

    return currentBerthCallIndex <= blockedBerthCallIndex;
};

export const hasRotationItemBlockedTank = (
    stowagePlan: StowagePlan,
    rotationPortCall: RotationPortCall | null,
    rotationBerthCall: RotationBerthCall
) => {
    const _hasRotationItemBlockedTank = stowagePlan?.blockedTanks?.some((t) => {
        if (rotationPortCall && !rotationBerthCall) {
            return t.fromPortCallGuid === rotationPortCall.portCallGuid || t.toPortCallGuid === rotationPortCall.portCallGuid;
        } else if (rotationBerthCall) {
            return t.fromBerthCallGuid === rotationBerthCall.berthCallGuid || t.toBerthCallGuid === rotationBerthCall.berthCallGuid;
        }
        return false;
    });

    return _hasRotationItemBlockedTank;
};
